import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CoreTable from '../../coreComponents/Table/Table.tsx';
import Button from '../../coreComponents/Button/Button.tsx';
import {
  deleteActiveDrivers,
  getAllDriver,
  getAllVerifiedDriver,
} from '../../services/DriverService.ts';
import { PAGE_SIZE } from '../../config.js';
import { exportToExcel } from '../../services/helpers.ts';

interface Vehicle {
  id: number;
  driverId: number;
  vehicle_no: string;
  vehicle_type: string;
}

interface DriverData {
  id: number;
  name: string;
  location: string;
  dob: string;
  driving_license_no: string;
  email: string | null;
  isVerified: boolean;
  isDeleted: boolean;
  phone: string;
  preferred_language: string;
  vehicles: Vehicle[];
}

const columns = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Name' },
  { id: 'location', label: 'Location' },
  { id: 'dob', label: 'DOB' },
  { id: 'driving_license_no', label: 'Driving License No' },
  { id: 'phone', label: 'Phone' },
  { id: 'email', label: 'Email' },
  { id: 'isVerified', label: 'Is Verified' },
  { id: 'isDeleted', label: 'Is Deleted' },
  { id: 'preferred_language', label: 'Preferred Language' },
];

const Driver: React.FC = () => {
  const navigate = useNavigate();
  const [driverData, setDriverData] = useState<DriverData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);

  const fetchDriverData = async (pageNum: number, refresh = false) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const response = await getAllVerifiedDriver(pageNum, PAGE_SIZE);

      if (response?.data?.data) {
        const newData = response.data.data;
        setTotalCount(response.data.total);

        if (pageNum === 1 || refresh) {
          setDriverData(newData);
        } else {
          setDriverData((prev) => [...prev, ...newData]);
        }

        setHasMore(
          newData.length === PAGE_SIZE &&
            (refresh ? newData.length : driverData.length + newData.length) <
              response.data.total
        );
      }
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        console.error('Unauthorized access. Redirecting to login...');
        navigate('/login');
      } else {
        setError('Failed to fetch drivers');
        console.log(err);
      }
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverData(1, true);
  }, []);

  const loadMore = () => {
    if (!isLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchDriverData(nextPage);
    }
  };

  const handleUpdate = (id: number) => {
    console.log(`Update driver with ID: ${id}`);
  };

  const handleDelete = async (id: number) => {
    const response = await deleteActiveDrivers(id);

    if (response.data) {
      const updatedDriverData = driverData.filter((driver) => driver.id !== id);
      setDriverData(updatedDriverData);
    }
  };

  const handleExportToExcel = async () => {
    const details = (await getAllDriver())?.data?.map(
      ({ id, name, email, phone, isVerified }) => {
        return {
          id,
          name,
          email,
          phone,
          isVerified,
        };
      }
    );

    exportToExcel(details, 'driverDetails');
  };

  if (loading && driverData.length === 0)
    return <div>Loading verified drivers...</div>;

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div style={{ padding: '16px' }}>
      <CoreTable
        columns={columns}
        data={driverData.map((driver) => ({
          ...driver,
          isDeleted: driver.isDeleted ? 'true' : 'false',
          isVerified: driver.isVerified ? 'true' : 'false',
        }))}
        infiniteScroll={true}
        hasMore={hasMore}
        loadMore={loadMore}
        totalCount={totalCount}
        showSearch={false}
      />
    </div>
  );
};

export default Driver;
